'use client';

import React, { useEffect, useRef, useState } from 'react';

import AppImage from '@/components/ui/AppImage';

interface LiveSession {
    title: string;
    instructor: string;
    schedule: string;
    level: string;
    category: string;
    categoryColor: string;
    img: string;
    alt: string;
    rating: number;
    students: string;
    type: 'live-slot' | 'bootcamp' | 'program';
    typeLabel: string;
    typeColor: string;
    price: string;
}

const allSessions: LiveSession[] = [
    {
        title: 'Nigerian Cooking Masterclass',
        instructor: 'Chef Amaka',
        schedule: 'Tue & Thu · 6pm',
        level: 'Beginner',
        category: 'Practical Skills',
        categoryColor: 'bg-orange/10 text-orange border-orange/20',
        img: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=500&q=80',
        alt: 'Nigerian cooking class with vibrant spices and ingredients on a bright wooden kitchen counter',
        rating: 4.9,
        students: '3.2k',
        type: 'live-slot',
        typeLabel: 'Live Slot',
        typeColor: 'text-red-500 bg-red-500/10 border-red-500/20',
        price: 'From ₦5,000/session'
    },
    {
        title: 'Full-Stack Web Development',
        instructor: 'Tunde Okafor',
        schedule: '8 weeks · Mon–Fri 7pm',
        level: 'Intermediate',
        category: 'Technology',
        categoryColor: 'bg-primary/10 text-primary border-primary/20',
        img: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=500&q=80',
        alt: 'Developer coding on laptop in bright modern workspace, clean desk with multiple screens',
        rating: 4.8,
        students: '5.1k',
        type: 'bootcamp',
        typeLabel: 'Bootcamp',
        typeColor: 'text-primary bg-primary/10 border-primary/20',
        price: '₦80,000 enrolment'
    },
    {
        title: 'SS3 Mathematics',
        instructor: 'Bright Academy',
        schedule: 'Mon–Fri · 9am',
        level: 'Secondary',
        category: 'Academic',
        categoryColor: 'bg-green/10 text-green border-green/20',
        img: "https://images.unsplash.com/photo-1657004508178-5374531174db",
        alt: 'Teacher explaining mathematics on whiteboard in a bright classroom with students',
        rating: 4.9,
        students: '1.2k',
        type: 'program',
        typeLabel: 'Program',
        typeColor: 'text-green bg-green/10 border-green/20',
        price: '₦30,000/term'
    },
    {
        title: 'Professional Hairdressing',
        instructor: 'Ngozi Eze',
        schedule: 'Sat & Sun · 10am',
        level: 'Beginner',
        category: 'Practical Skills',
        categoryColor: 'bg-coral/10 text-coral border-coral/20',
        img: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=500&q=80',
        alt: 'Hairstylist demonstrating braiding technique in a bright modern salon with natural light',
        rating: 4.9,
        students: '2.8k',
        type: 'live-slot',
        typeLabel: 'Live Slot',
        typeColor: 'text-red-500 bg-red-500/10 border-red-500/20',
        price: 'From ₦7,500/session'
    },
    {
        title: 'Digital Marketing Bootcamp',
        instructor: 'Emeka Obi',
        schedule: '6 weeks · Tue & Thu 6pm',
        level: 'Beginner',
        category: 'Business',
        categoryColor: 'bg-green/10 text-green border-green/20',
        img: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=500&q=80',
        alt: 'Marketing professional teaching digital strategy in a bright open-plan office with large windows',
        rating: 4.7,
        students: '6.3k',
        type: 'bootcamp',
        typeLabel: 'Bootcamp',
        typeColor: 'text-primary bg-primary/10 border-primary/20',
        price: '₦60,000 enrolment'
    },
    {
        title: 'Carpentry & Woodworking',
        instructor: 'Ade Builders Institute',
        schedule: 'Mon, Wed & Fri · 8am',
        level: 'Beginner',
        category: 'Vocational',
        categoryColor: 'bg-accent/10 text-accent border-accent/20',
        img: "https://images.unsplash.com/photo-1578058405567-53aea0a6499b",
        alt: 'Carpenter teaching woodworking in a workshop, sawdust in air, warm workshop lighting',
        rating: 4.8,
        students: '890',
        type: 'program',
        typeLabel: 'Program',
        typeColor: 'text-green bg-green/10 border-green/20',
        price: '₦45,000/term'
    }];


const filterTabs = [
    { label: 'All', value: 'all' },
    { label: 'Live Slots', value: 'live-slot' },
    { label: 'Bootcamps', value: 'bootcamp' },
    { label: 'Programs', value: 'program' }];


const categoryList = [
    { label: 'Technology', icon: '💻', color: 'bg-primary', count: '95+' },
    { label: 'Practical Skills', icon: '🔨', color: 'bg-accent', count: '120+' },
    { label: 'Academic', icon: '📚', color: 'bg-green', count: '240+' },
    { label: 'Creative', icon: '🎨', color: 'bg-purple', count: '75+' },
    { label: 'Business', icon: '💼', color: 'bg-green', count: '60+' },
    { label: 'Vocational', icon: '🔧', color: 'bg-accent', count: '80+' }];


export default function LearnCategoriesSection() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [activeFilter, setActiveFilter] = useState('all');

    const filteredSessions = activeFilter === 'all' ?
        allSessions :
        allSessions.filter((s) => s.type === activeFilter);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.08 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section id="categories" ref={sectionRef} className="relative py-20 md:py-28 bg-background overflow-hidden">
            <div className="absolute inset-0 grid-bg pointer-events-none opacity-60" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] blob-orange opacity-20 -translate-x-1/4 translate-y-1/4 pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
                    <div className="max-w-2xl">
                        <div className="reveal mb-4">
                            <span className="pill-tag border border-primary/20 text-primary bg-primary/8 text-xs uppercase tracking-wider">
                                Live sessions
                            </span>
                        </div>
                        <h2 className="reveal text-section-title font-extrabold text-foreground delay-100">
                            Find your next live class.
                        </h2>
                        <p className="reveal text-lg text-muted-foreground font-body leading-relaxed mt-3 max-w-xl delay-200">
                            Browse live sessions, bootcamps, and structured programs. Every class is scheduled and taught live.
                        </p>
                    </div>
                </div>

                {/* Filter tabs */}
                <div className="reveal delay-200 flex flex-wrap gap-2 mb-8">
                    {filterTabs.map((tab) =>
                        <button
                            key={tab.value}
                            onClick={() => setActiveFilter(tab.value)}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${activeFilter === tab.value ?
                                    'bg-primary text-primary-foreground border-primary shadow-blue' :
                                    'bg-card text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'}`
                            }>

                            {tab.label}
                        </button>
                    )}
                </div>

                {/* Sessions grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {filteredSessions.map((session, i) =>
                        <div
                            key={session.title}
                            className="reveal card-hover cursor-pointer"
                            style={{ transitionDelay: `${i * 80}ms` }}>

                            <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-card group">
                                {/* Image */}
                                <div className="h-44 overflow-hidden relative">
                                    <AppImage
                                        src={session.img}
                                        alt={session.alt}
                                        width={400}
                                        height={176}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute top-3 left-3">
                                        <span className={`pill-tag border ${session.categoryColor} bg-card`}>
                                            {session.category}
                                        </span>
                                    </div>
                                    <div className="absolute top-3 right-3">
                                        <span className={`pill-tag border text-xs font-bold ${session.typeColor} bg-white/90 backdrop-blur-sm`}>
                                            {session.type === 'live-slot' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1 animate-pulse" />}
                                            {session.typeLabel}
                                        </span>
                                    </div>
                                </div>
                                {/* Content */}
                                <div className="p-5">
                                    <h3 className="text-card-title font-bold text-card-foreground mb-1 group-hover:text-primary transition-colors duration-200">
                                        {session.title}
                                    </h3>
                                    <p className="text-muted-foreground text-sm font-body mb-1">
                                        {session.level} · {session.instructor}
                                    </p>
                                    <p className="text-primary text-xs font-semibold mb-3">
                                        🗓 {session.schedule}
                                    </p>
                                    <div className="flex items-center justify-between pt-3 border-t border-border">
                                        <div className="flex items-center gap-1.5">
                                            <svg className="w-4 h-4 text-yellow-400 fill-yellow-400" viewBox="0 0 24 24">
                                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                            </svg>
                                            <span className="text-foreground font-bold text-sm">{session.rating}</span>
                                        </div>
                                        <span className="text-muted-foreground text-xs font-body">{session.students} students</span>
                                    </div>
                                    <div className="mt-3">
                                        <span className="text-foreground font-semibold text-sm">{session.price}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Category bento */}
                <div className="mt-16 md:mt-20">
                    <div className="reveal mb-8">
                        <h2 className="text-section-title font-extrabold text-foreground">
                            Every subject. Every skill. All live.
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                        {categoryList.map((cat, i) =>
                            <div
                                key={cat.label}
                                className="reveal card-hover cursor-pointer"
                                style={{ transitionDelay: `${i * 60}ms` }}>

                                <div className={`${cat.color} rounded-2xl p-5 flex flex-col items-center text-center gap-2 group overflow-hidden relative min-h-[120px] justify-center`}>
                                    <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300" />
                                    <span className="text-3xl">{cat.icon}</span>
                                    <p className="font-bold text-white text-sm">{cat.label}</p>
                                    <span className="pill-tag border border-white/30 text-white/80 bg-white/10 text-xs">{cat.count}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>);

}